/* eslint-disable */
import { useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import { notificationServices } from '@/Services';
import aiPackaging from '@/Services/aiPackaging.services';
import cogsAnalyticsServices from '@/Services/Analytics Services/cogsAnalytics.services';
import { formatDate, handleResponseStreamDownload, serializeVisibilityForAmountCaption } from '@/Helpers';
import { useDispatch } from 'react-redux';
import { analyticsAction } from '@/Actions';

export const useAiFmsData = (props) => {
	const { parentTab, formStore, toPodFilter } = props;
	const [rawData, setRawData] = useState([]);
	const [producedData, setProducedData] = useState([]);
	const [issuedData, setIssuedData] = useState([]);
	const [statsSummary, setStatsSummary] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(analyticsAction?.getShifts());
	}, []);

	const checkNull = useCallback((value) => {
		if ([-999, '-999', undefined, null, ''].includes(value)) {
			return null;
		}
		if (Number(value) === 0) {
			return null;
		}
		return Number(value);
	}, []);

	const fetchWastageSummary = useCallback(async (filter) => {
		setIsLoading(true);
		const dateRange = filter?.dateRange;
		const req = {
			data: {
				filters: {
					dateRangeFilter: {
						from: moment(dateRange?.from).format('YYYY-MM-DD'),
						to: moment(dateRange?.to).format('YYYY-MM-DD'),
					},
				},
			},
		};
		try {
			const response = await aiPackaging?.fetchWastageSummary(req);
			if (response?.statusCode === 200) {
				const { data } = response;
				setRawData(data);
			} else {
				notificationServices?.generateNotification({
					type: 'error',
					message: response?.message,
				});
			}
		} catch (error) {
			notificationServices?.generateNotification({
				type: 'error',
				message: error.message || 'Failed to fetch data',
			});
		} finally {
			setIsLoading(false);
		}
	}, [parentTab, checkNull, formStore, toPodFilter]);

	const fetchData = useCallback(async (filter, tab = parentTab, skuType) => {
		if (tab?.id === 4) {
			fetchWastageSummary(filter, tab);
			return;
		}
		if (!filter?.fromPodFilter === '' ||
			filter?.fromPodFilter === null) {
			notificationServices?.generateNotification({
				type: 'error',
				message: 'Kitchen is Mandatory',
			});
			return;
		}

		setIsLoading(true);
		const dateRange = filter?.dateRange;
		const req = {
			data: {
				filters: {
					dateRangeFilter: {
						from: moment(dateRange?.from).format('YYYY-MM-DD'),
						to: moment(dateRange?.to).format('YYYY-MM-DD'),
					},
					fromStore: filter?.fromPodFilter?.id
						? checkNull(filter?.fromPodFilter?.id)
						: checkNull(formStore),
					storeId: filter?.toPodFilter?.id
						? checkNull(filter?.toPodFilter?.id)
						: checkNull(toPodFilter),
					analysisType: tab?.value,
					skuType: skuType,
				},
			},
		};

		try {
			const response = await aiPackaging?.aiPackagingGetDataWithHeaders(req);
			if (response?.statusCode === 200) {
				const { data } = response;
				const { mealWiseScanData } = data;
				setRawData(mealWiseScanData);
			} else {
				notificationServices?.generateNotification({
					type: 'error',
					message: response?.message,
				});
			}
		} catch (error) {
			notificationServices?.generateNotification({
				type: 'error',
				message: error.message || 'Failed to fetch data',
			});
		} finally {
			setIsLoading(false);
		}
	}, [parentTab, checkNull, formStore, toPodFilter]);

	const fetchProducedData = useCallback(async (filter) => {
		if (!filter?.fromPodFilter === '' ||
			filter?.fromPodFilter === null) {
			notificationServices?.generateNotification({
				type: 'error',
				message: 'Kitchen is Mandatory',
			});
			return;
		}

		const dateRange = filter?.dateRange;
		const req = {
			data: {
				filters: {
					dateRangeFilter: {
						from: moment(dateRange?.from).format('YYYY-MM-DD'),
						to: moment(dateRange?.to).format('YYYY-MM-DD'),
					},
					fromStore: filter?.fromPodFilter?.id
						? checkNull(filter?.fromPodFilter?.id)
						: checkNull(formStore),
					storeId: filter?.toPodFilter?.id
						? checkNull(filter?.toPodFilter?.id)
						: checkNull(toPodFilter),
					analysisType: 'PRODUCED',
				},
			},
		};

		try {
			const response = await aiPackaging?.aiPackagingGetDataWithHeaders(req);
			if (response?.statusCode === 200) {
				const { data } = response;
				const { mealWiseScanData } = data;
				setProducedData(mealWiseScanData);
			} else {
				notificationServices?.generateNotification({
					type: 'error',
					message: response?.message,
				});
			}
		} catch (error) {
			notificationServices?.generateNotification({
				type: 'error',
				message: error.message || 'Failed to fetch produced data',
			});
		}
	}, [checkNull, formStore, toPodFilter]);

	const fetchIssuedData = useCallback(async (filter) => {
		if (!filter?.fromPodFilter === '' ||
			filter?.fromPodFilter === null) {
			notificationServices?.generateNotification({
				type: 'error',
				message: 'Kitchen is Mandatory',
			});
			return;
		}

		const dateRange = filter?.dateRange;
		const req = {
			data: {
				filters: {
					dateRangeFilter: {
						from: moment(dateRange?.from).format('YYYY-MM-DD'),
						to: moment(dateRange?.to).format('YYYY-MM-DD'),
					},
					fromStore: filter?.fromPodFilter?.id
						? checkNull(filter?.fromPodFilter?.id)
						: checkNull(formStore),
					storeId: filter?.toPodFilter?.id
						? checkNull(filter?.toPodFilter?.id)
						: checkNull(toPodFilter),
					analysisType: 'ISSUED',
				},
			},
		};

		try {
			const response = await aiPackaging?.aiPackagingGetDataWithHeaders(req);
			if (response?.statusCode === 200) {
				const { data } = response;
				const { mealWiseScanData } = data;
				setIssuedData(mealWiseScanData);
			} else {
				notificationServices?.generateNotification({
					type: 'error',
					message: response?.message,
				});
			}
		} catch (error) {
			notificationServices?.generateNotification({
				type: 'error',
				message: error.message || 'Failed to fetch produced data',
			});
		}
	}, [checkNull, formStore, toPodFilter]);

	const fetchCogsAnalysisGraph = useCallback(async (dateRange, shiftId, selectedTab) => {
		try {
			const apiCalls = [];
			const basePayload = {
				avoidGraph: true,
				grouping: {
					active: true,
					type: 'geographyWise',
					currentSelection: 'brand',
					selection: {},
				},
				shiftId: [-999, 999].includes(shiftId) ? null : shiftId,
				analysisType: 'BOM',
			};

			const today = moment().startOf('day');
			apiCalls.push(
				cogsAnalyticsServices?.fetchCogsAnalysisGraph({
					req: {
						data: {
							...basePayload,
							yAxis: '',
							dateRangeFilter: {
								from: moment(dateRange?.from).format('YYYY-MM-DD'),
								to: moment(dateRange?.to).format('YYYY-MM-DD'),
							},
						},
					},
				})
			);

			if (
				today.isBetween(
					moment(dateRange?.from).startOf('day'),
					moment(dateRange?.to).startOf('day'),
					null,
					'[]'
				)
			) {
				apiCalls.push(
					cogsAnalyticsServices?.fetchCogsAnalysisGraphToday({
						req: {
							data: {
								...basePayload,
								yAxis: '',
								dateRangeFilter: {
									from: today.format('YYYY-MM-DD'),
									to: today.format('YYYY-MM-DD'),
								},
							},
						},
					})
				);
			}

			const [response, responseToday] = await Promise.all(apiCalls);

			if (response?.statusCode === 200) {
				const mergedStatsData = mergeStatsData(
					response?.data?.statsData,
					responseToday?.data?.statsData
				);
				const processedStats = processStatsData(mergedStatsData);
				const statsSummary = Object?.keys(processedStats)?.map((ele) => {
					return serializeVisibilityForAmountCaption(processedStats, ele);
				});
				setStatsSummary(statsSummary);
			}
		} catch (error) {
			notificationServices?.generateNotification({
				type: 'error',
				message: 'Failed to fetch analytics data',
			});
		}
	}, []);

	const mergeStatsData = useCallback((historicalData, todayData) => {
		if (!historicalData) return {};
		if (!todayData) return historicalData;

		const mergedData = {
			...historicalData,
		};

		if (Array.isArray(todayData)) {
			todayData.forEach((todayItem) => {
				const historicalKey = Object.keys(historicalData).find(
					(key) => historicalData[key].label === todayItem.label
				);

				if (
					historicalKey &&
					typeof historicalData[historicalKey].value === 'number' &&
					typeof todayItem.value === 'number'
				) {
					mergedData[historicalKey] = {
						...historicalData[historicalKey],
						value: historicalData[historicalKey].value + todayItem.value,
					};
				}
			});
		} else {
			Object.keys(historicalData).forEach((key) => {
				if (
					todayData[key] &&
					typeof historicalData[key].value === 'number' &&
					typeof todayData[key].value === 'number'
				) {
					mergedData[key] = {
						...historicalData[key],
						value: historicalData[key].value + todayData[key].value,
					};
				}
			});
		}

		return mergedData;
	}, []);

	const processStatsData = useCallback((statsData) => {
		if (!statsData) return statsData;

		const processedData = {
			...statsData,
		};
		const idealCostKey = 3;

		if (!idealCostKey || !processedData[idealCostKey]?.value) {
			return processedData;
		}

		const idealCost = processedData[idealCostKey].value;

		Object.keys(processedData).forEach((key) => {
			if (key !== idealCostKey && processedData[key].value && idealCost > 0) {
				const percentage = Math.round(
					(processedData[key].value / idealCost) * 100
				);
				processedData[key] = {
					...processedData[key],
					percentageValue: percentage,
				};
			}
			if (
				['1', '2', '3', 1, 2, 3].includes(key) &&
				processedData[key].percentageValue !== undefined
			) {
				processedData[key].percentageValue = undefined;
			}
		});

		return processedData;
	}, []);

	const handleExport = async (filters, tab) => {
		const fromDate = filters?.dateRange?.from;
		const toDate = filters?.dateRange?.to;
		const formattedFromDate = formatDate(fromDate);
		const formattedToDate = formatDate(toDate);
		if (formattedFromDate === '' || formattedToDate === '' || formattedFromDate === null || formattedToDate === null) {
			notificationServices?.generateNotification({
				type: 'error',
				message: 'Date is Mandatory',
			});
			return;
		}
		const req = {
			data: {
				filters: {
					dateRangeFilter: {
						from: formattedFromDate,
						to: formattedToDate,
					},
					fromStore: filters?.fromPodFilter?.id
						? checkNull(filters?.fromPodFilter?.id)
						: checkNull(formStore),
					storeId: filters?.toPodFilter?.id
						? checkNull(filters?.toPodFilter?.id)
						: checkNull(toPodFilter),
					analysisType: tab?.value,

				},
			},
		};
		try {
			const response = await aiPackaging?.aiPacakgingExport(req);
			handleResponseStreamDownload(response, `${tab?.value} ${formattedFromDate} to ${formattedToDate}.csv`);
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error,
			});
		} finally {
			setIsLoading(false);
		}
	};

	return {
		rawData,
		producedData,
		issuedData,
		statsSummary,
		isLoading,
		fetchData,
		fetchProducedData,
		fetchCogsAnalysisGraph,
		fetchIssuedData,
		handleExport,
		fetchWastageSummary,
	};
};
